import {
	OracleSource,
	SpotBalanceType,
	UserAccount,
	ZERO,
	positionIsAvailable,
} from '@drift-labs/sdk';
import { ENUM_UTILS, MarketId, matchEnum } from '@drift/common';
import { PERP_MARKETS_LOOKUP } from '../../environmentVariables/EnvironmentVariables';
import { PRE_MARKET_ACKNOWLEDGED_WARNING_KEY } from './PreLaunchMarketConstants';
import InfoMessage from '../TradeForm/InfoMessage';

export type PRE_MARKET_ALERT_TO_SHOW =
	| 'acknowledge_terms'
	| 'cant_use_current_account';

export const getIsPreLaunchMarket = (marketId: MarketId) => {
	if (marketId.isSpot) return false;

	const marketConfig = PERP_MARKETS_LOOKUP[marketId.marketIndex];

	return ENUM_UTILS.match(marketConfig.oracleSource, OracleSource.Prelaunch);
};

const userHasNonPreLaunchMarketPerpPositions = (userAccount: UserAccount) => {
	if (!userAccount || !userAccount?.perpPositions) {
		return false;
	}

	const nonZeroNonPreLaunchMarketPositions = userAccount.perpPositions.filter(
		(position) => {
			const positionIsZero = positionIsAvailable(position);
			if (positionIsZero) return false;

			const isPreLaunchMarketPosition = getIsPreLaunchMarket(
				MarketId.createPerpMarket(position.marketIndex)
			);

			if (isPreLaunchMarketPosition) {
				return false;
			}

			return true;
		}
	);

	return nonZeroNonPreLaunchMarketPositions.length > 0;
};

const userHasPreLaunchMarketPerpPosition = (userAccount: UserAccount) => {
	if (!userAccount || !userAccount?.perpPositions) {
		return false;
	}

	const nonZeroPreLaunchMarketPositioins = userAccount.perpPositions.filter(
		(position) => {
			const positionIsZero = positionIsAvailable(position);
			if (positionIsZero) return false;

			return getIsPreLaunchMarket(
				MarketId.createPerpMarket(position.marketIndex)
			);
		}
	);

	return nonZeroPreLaunchMarketPositioins.length > 0;
};

const userHasBorrows = (userAccount: UserAccount) => {
	if (!userAccount || !userAccount?.spotPositions) return false;

	const openBorrows = userAccount?.spotPositions?.filter((spotPosition) => {
		return (
			!spotPosition.scaledBalance.eq(ZERO) &&
			matchEnum(spotPosition.balanceType, SpotBalanceType.BORROW)
		);
	});

	return openBorrows && openBorrows.length > 0;
};

/**
 * Returns whether the input user account should be blocked from trading in a pre-launch market
 * @param userAccount
 * @returns
 */
export const shouldBlockTradingForPreLaunchMarket = (
	userAccount: UserAccount
) => {
	if (!userAccount) return false;

	const hasMarginEnabled = userAccount.isMarginTradingEnabled;
	const hasBorrows = userHasBorrows(userAccount);
	const hasPerpPositions = userHasNonPreLaunchMarketPerpPositions(userAccount);

	return hasMarginEnabled || hasBorrows || hasPerpPositions;
};

/**
 * Returns whether the user should be blocked from trading regular markets due to having a pre-launch market position
 * @param userAccount
 * @returns
 */
export const shouldBlockTradingForNonPreLaunchMarket = (
	userAccount: UserAccount
) => {
	if (!userAccount) return false;

	return userHasPreLaunchMarketPerpPosition(userAccount);
};

export const getShouldBlockBorrows = (userAccount: UserAccount) => {
	return userHasPreLaunchMarketPerpPosition(userAccount);
};

export const checkIfPreLaunchMarketWarningRequired =
	(): PRE_MARKET_ALERT_TO_SHOW => {
		const preLaunchMarketWarningAcknowledged = localStorage.getItem(
			PRE_MARKET_ACKNOWLEDGED_WARNING_KEY
		);

		if (!preLaunchMarketWarningAcknowledged) {
			return 'acknowledge_terms';
		}
		return null;
	};

export const BorrowsDisabledMessage = () => {
	return (
		<InfoMessage
			type="error"
			messageTitle={`Non-USDC Borrows are disabled for this subaccount`}
			message={`As a protocol safety measure, non-USDC borrows are disabled when you have an open position in a Pre-Launch Market. To open a new borrow, use a separate subaccount or close your open perpetuals position.`}
		/>
	);
};
